import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent } from '@/components/ui';
import { formatDate, formatCents } from '@/lib/utils';
import type { AdminSubscription } from '@/types/admin-subscription';

interface SubscriptionDetailCardProps {
  subscription: AdminSubscription;
  onTerminate: () => void;
  isTerminating: boolean;
}

function formatInterval(interval: string, t: (key: string) => string): string {
  const key = `intervals.${interval}`;
  const translated = t(key);
  return translated === key ? interval : translated;
}

export function SubscriptionDetailCard({
  subscription: sub,
  onTerminate,
  isTerminating,
}: SubscriptionDetailCardProps) {
  const { t } = useTranslation('subscriptions');

  return (
    <Card className="m-2 border-muted">
      <CardContent className="grid gap-3 py-4 md:grid-cols-2">
        <div>
          <p className="text-sm text-muted-foreground">{t('detail.transaction')}</p>
          <Link
            to={`/transactions/${sub.transactionId}`}
            className="font-mono text-sm text-primary hover:underline"
          >
            {sub.transactionId}
          </Link>
        </div>

        {sub.destination && (
          <div>
            <p className="text-sm text-muted-foreground">{t('detail.msisdn')}</p>
            <p className="font-mono text-sm">{sub.destination}</p>
          </div>
        )}

        {sub.operatorId && (
          <div>
            <p className="text-sm text-muted-foreground">{t('detail.operator')}</p>
            <p>{sub.operatorId}</p>
          </div>
        )}

        {sub.nextChargeAt && sub.status === 'active' && (
          <div>
            <p className="text-sm text-muted-foreground">{t('detail.nextCharge')}</p>
            <p>{formatDate(sub.nextChargeAt)}</p>
          </div>
        )}

        <div>
          <p className="text-sm text-muted-foreground">{t('detail.amount')}</p>
          <p>
            {formatCents(sub.grossAmount)} / {formatInterval(sub.subscriptionInterval, t)}
          </p>
        </div>

        <div>
          <p className="text-sm text-muted-foreground">{t('detail.chargeCount')}</p>
          <p>{sub.chargeCount}</p>
        </div>

        <div>
          <p className="text-sm text-muted-foreground">{t('detail.created')}</p>
          <p>{formatDate(sub.createdAt)}</p>
        </div>

        {sub.updatedAt && (
          <div>
            <p className="text-sm text-muted-foreground">{t('detail.updated')}</p>
            <p>{formatDate(sub.updatedAt)}</p>
          </div>
        )}

        {sub.terminatedAt && (
          <div>
            <p className="text-sm text-muted-foreground">{t('detail.terminatedAt')}</p>
            <p>{formatDate(sub.terminatedAt)}</p>
          </div>
        )}

        {sub.terminationReason && (
          <div>
            <p className="text-sm text-muted-foreground">{t('detail.terminationReason')}</p>
            <p>{sub.terminationReason}</p>
          </div>
        )}

        {sub.status === 'active' && (
          <div className="md:col-span-2 flex justify-end pt-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={onTerminate}
              disabled={isTerminating}
            >
              {t('terminate.submit')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
